 /* istanbul ignore next */
// DSS Documentation on SocoModalService - http://tiny.sc/cgsocomodalservice
// DSS Documentation on SocoModalComponent - http://tiny.sc/cgsocomodalcomponent
import { Injectable } from '@angular/core';
import { Subject ,  Observable } from 'rxjs';

// DSS Documentation on SocoAlertService - http://tiny.sc/cgsocoalertservice
import { SocoAlertService } from '@app/soco/alert';

@Injectable()

export class SocoModalService {
    /* istanbul ignore next */
    private modalShowSource = new Subject<SocoModalServiceOptions>();
    private modalHideSource = new Subject<boolean>();
    private modalConfirmationSource = new Subject<boolean>();
    modalShown$ = this.modalShowSource.asObservable();
    modalHidden$ = this.modalHideSource.asObservable();
    modalConfirmation$ = this.modalConfirmationSource.asObservable();

    // DSS Documentation on SocoAlertService - http://tiny.sc/cgsocoalertservice
    constructor(private socoAlertService: SocoAlertService) { }

    /* istanbul ignore next */
    /**
     *  Shows the modal with the specified id
     *  @param options Primarily only id is needed. However, specify the options needed for the modal to display and function properly.
     */
    show(options: SocoModalServiceShowOptions): void {
        // hide any alerts from the last time the modal was open
        // DSS Documentation on SocoAlertService - http://tiny.sc/cgsocoalertservice
        this.socoAlertService.hide();

        // show the modal
        $('#' + options.id).modal('show');

        // attach the callback for when the modal is closed
        if (options.onClose) {
            $('#' + options.id).unbind('hidden.bs.modal').on('hidden.bs.modal', options.onClose);
        }

        $('#' + options.id).unbind('shown.bs.modal').on('shown.bs.modal', () => {
            // set focus on the first form element in the modal
            $('#' + options.id + ' :input:visible:enabled:first').focus();

            // attach to keydown so that if enter key is pressed we can simulate a click of the primary button
            if (!options.dontSubmitWhenEnterPressed) {
                $('#' + options.id + ' input[type="text"]').keydown(function (e) {
                    if (e.keyCode === 13) {
                        e.preventDefault();
                        $('#' + options.id + ' .modal-footer .btn.btn-primary').eq(0).click();
                    }
                });
            }

            // call the callback for when the modal is opened
            if (options.onOpen) {
                options.onOpen();
            }
        });
    }

    /* istanbul ignore next */
    /**
     *  Used to represent a messaging error in the application caused by the system/application
     *  @param options Primarily only title and body are needed. However, specify the options needed for the modal to update properly.
     */
    fail(options: SocoModalServiceOptions): void {
        // merge the options passed in with the default options
        let settings = {
            title: 'Oops something went wrong!',
            message: 'Unknown error occurred',
            type: SocoModalType.Failure
        };
        settings = $.extend(settings, options);
        settings.type = SocoModalType.Failure;

        // show the modal
        this.modalShowSource.next(settings);
    }

    /* istanbul ignore next */
    /**
    *  Used to represent a messaging error in the application caused by the user
    *  @param options Primarily only title and body are needed. However, specify the options needed for the modal to update properly.
    */
    error(options: SocoModalServiceOptions): void {
        // merge the options passed in with the default options
        let settings = {
            title: 'Oops something went wrong!',
            message: 'Unknown error occurred',
            type: SocoModalType.Error
        };
        settings = $.extend(settings, options);
        settings.type = SocoModalType.Error;

        // show the modal
        this.modalShowSource.next(settings);
    }

    /* istanbul ignore next */
    /**
    *  Used to caution a user to yield and address a state that could prevent an error
    *  @param options Primarily only title and body are needed. However, specify the options needed for the modal to update properly.
    */
    caution(options: SocoModalServiceOptions): void {
        // merge the options passed in with the default options
        let settings = {
            title: 'Watch Out!',
            message: 'Be careful before proceeding',
            type: SocoModalType.Caution,
        };
        settings = $.extend(settings, options);
        settings.type = SocoModalType.Caution;

        // show the modal
        this.modalShowSource.next(settings);
    }

    /* istanbul ignore next */
    /**
    * Used to represent data that will inform the user about a specific contextual situation within the system.
    *  @param options Primarily only title and body are needed. However, specify the options needed for the modal to update properly.
    */
    info(options: SocoModalServiceOptions): void {
        // merge the options passed in with the default options
        let settings = {
            title: 'Take a look at this!',
            message: 'Information goes here',
            type: SocoModalType.Information
        };
        settings = $.extend(settings, options);
        settings.type = SocoModalType.Information;

        // show the modal
        this.modalShowSource.next(settings);
    }

    /* istanbul ignore next */
    /**
    * Used to represent that the user successfully performed system input
    *  @param options Primarily only title and body are needed. However, specify the options needed for the modal to update properly.
    */
    success(options: SocoModalServiceOptions): void {
        // merge the options passed in with the default options
        let settings = {
            title: 'Success!',
            message: 'Successfully completed the specified operation',
            type: SocoModalType.Success
        };
        settings = $.extend(settings, options);
        settings.type = SocoModalType.Success;

        // show the modal
        this.modalShowSource.next(settings);
    }

    /* istanbul ignore next */
    /**
    * Displays a modal with a prompt and Yes and No buttons to allow the user to confirm or cancel the operation
    *  @param options Primarily only title, body and confirmationCallback are needed.
    *  However, specify the options needed for the modal to update properly.
    */
    confirm(options: SocoModalServiceOptions): void {
        // merge the options passed in with the default options
        let settings = {
            title: 'Are you sure about that?',
            body: 'Please confirm your decision',
            dontCloseOnChoice: false,
            isConfirmationModal: true,
            isModal: true,
            type: SocoModalType.Caution
        };
        settings = $.extend(settings, options);
        settings.type = SocoModalType.Caution;
        settings.isConfirmationModal = true;

        // show the modal
        this.modalShowSource.next(settings);
    }

    /* istanbul ignore next */
    setModalChoice(choice: boolean) { this.modalConfirmationSource.next(choice); }

    /* istanbul ignore next */
    /**
    * Displays a static modal that cannot be closed with a loading icon and message
    *  @param options Primarily only title and body are needed. However, specify the options needed for the modal to update properly.
    */
    pleaseWait(options: SocoModalServiceOptions): SocoModalPleaseWaitData {
        // check if something else called please wait and has already hidden a modal
        let currentModal = $('.modal.in.soco-modal-hidden-please-wait');
        let modalBackdrop = $('.modal-backdrop.in.soco-modal-hidden-please-wait');
        if (currentModal.length < 1) {
            // hide any other modal that is displayed
            currentModal = $('.modal.in');
            modalBackdrop = $('.modal-backdrop');
            if (currentModal.length > 0) {
                // verify if the other modal is a soco-modal, if it is we are going to
                // replace it so we dont need to hold on to a reference to it
                if (currentModal.attr('id') === 'soco-modal') {
                    currentModal = null;
                    modalBackdrop = null;
                } else {
                    currentModal.hide().addClass('soco-modal-hidden-please-wait');
                    modalBackdrop.hide().addClass('soco-modal-hidden-please-wait');
                }
            }
        }

        // merge the options passed in with the default options
        let settings = {
            title: 'Please wait...',
            // tslint:disable-next-line:max-line-length
            body: '<div class="soco-modal-please-wait-body"><i class="fa-spinner fa-3x fa-spin"></i><h3>One moment please while we process your request</h3></div>',
            type: SocoModalType.Information,
            isModal: true,
            isStatic: true
        };
        if (options.body) {
            options.body = '<div class="soco-modal-please-wait-body"><h3>' + options.body
            + '</h3><i class="fa-spinner fa-3x fa-spin"></i></div>';
        }
        settings = $.extend(settings, options);
        settings.type = SocoModalType.Information;
        settings.isModal = true;
        settings.isStatic = true;

        // show the please wait modal
        this.modalShowSource.next(settings);

        // return data needed to restore
        return {
            hiddenModal: currentModal,
            hiddenModalBackdrop: modalBackdrop
        };
    }

    /* istanbul ignore next */
    /**
    * Hides the please wait modal and optionally shows any modals that were being displayed before the please wait modal was shown
    *  @param data Contains information about any modal that was being shown at the time the please wait modal was shown
    */
    doneWaiting(data: SocoModalPleaseWaitData): void {
        // if a modal is hidden then show it, otherwise just hide the please wait modal
        if (data && data.hiddenModal && data.hiddenModal.length > 0) {
            this.modalHideSource.next(false);
            data.hiddenModal.removeClass('soco-modal-hidden-please-wait').show();
            data.hiddenModalBackdrop.removeClass('soco-modal-hidden-please-wait').show();
        } else {
            this.hide();
        }
    }

    /* istanbul ignore next */
    /** Hides any and all modals */
    hide(): void {
        $('.modal.in').modal('hide');
    }

    /* istanbul ignore next */
    /** Indicates if any modal is open */
    isModalOpen(): boolean {
        return $('.modal').hasClass('in');
    }
}

/* istanbul ignore next */
export interface SocoModalServiceOptions {
    /** The title of the modal that will appear in the header */
    title?: string;

    /** The body of the modal. You can specify HTML or just text. */
    body?: string;

    /** This will be set for you if you call SocoModalService.confirm.
     *  Indicates if the modal should offer Yes/No buttons to confirm or deny. */
    isConfirmationModal?: boolean;

    /** This will be set for you if you call SocoModalService.pleaseWait.
     *  Indicates if to hide the header and footer buttons. */
    isStatic?: boolean;

    /** This will be set for you if you call SocoModalService.pleaseWait.
     *  Indicates if the modal should remain open when the backdrop is clicked. */
    isModal?: boolean;

    /** This is only needed if this is a confirmation modal. The function that will
     *  be called when the user chooses to confirm or deny.
     *  @param choice True if the user confirmed, false if the user denied. */
    // confirmationCallback?: (choice: boolean) => void; // Updated by John to use Observable

    /** This is only needed if this is a confirmation modal. You do not have to specify this. By
     *  default the value will be set to true and the modal will be closed when the user confirms
     *  or denies. However, if you want to leave the modal open when the user confirms or denies
     *  (perhaps because you want to avoid the modal close and re-open animation if once the user
     *   confirms you will show another modal) you can set this to true. */
    dontCloseOnChoice?: boolean;

    /** Identifies the purpose of the modal */
    type?: SocoModalType;

    // Added by John Hakeem
    headerCssClass?: string;
    buttonCssClass?: string;
    okButton?: string;
    cancelButton?: string;
    confirmButton?: string;
}

/* istanbul ignore next */
export interface SocoModalServiceShowOptions {
    /**
     *  The id of the modal to show
     */
    id: string;
    /**
     *  Called when the modal is closed
     */
    onClose?: () => void;
    /**
     *  Called when the modal is opened
     */
    onOpen?: () => void;
    /**
     *  Indicates if pressing enter should not simulate a click of the primary button
     */
    dontSubmitWhenEnterPressed?: boolean;
}

/* istanbul ignore next */
export interface SocoModalPleaseWaitData {
    hiddenModal: JQuery;
    hiddenModalBackdrop: JQuery;
}

/* istanbul ignore next */
export enum SocoModalType {
    Unknown = 0,
    Error = 1,
    Failure = 2,
    Caution = 3,
    Information = 4,
    Success = 5
}
